"use client";

import { useRouter } from "next/navigation";
import { useEffect, useRef } from "react";

import { logout } from "@/features/auth/services/auth-service";

export const IDLE_LOGOUT_MS = 30 * 60 * 1000;

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "wheel", "scroll", "touchstart"] as const;

/**
 * Logs the user out and redirects to login after IDLE_LOGOUT_MS with no
 * mouse/keyboard/scroll activity. Deliberately ignores background API
 * polling (e.g. the analysis-screen status poll) since that isn't real use.
 */
export function useIdleLogout(enabled: boolean, tenantCode: string): void {
  const router = useRouter();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!enabled) return;

    function resetTimer() {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        void logout()
          .catch(() => {})
          .then(() => {
            router.replace(`/${tenantCode}/login`);
            router.refresh();
          });
      }, IDLE_LOGOUT_MS);
    }

    resetTimer();
    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, resetTimer, { passive: true });
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, resetTimer);
      }
    };
  }, [enabled, tenantCode, router]);
}
